import { getNextLearningResource } from 'pages/LearningResources/Playlist/utils';
import PlaylistCard from './PlaylistCard';
import { PlaylistCurrentTrack } from 'pages/LearningResources/Playlist/PlaylistDetailPage';
import { PlaylistRecord, LearningTracks } from 'types/LearningResources/types';
import useCurrentUser from 'hooks/useCurrentUser';
import useUserEngagementEvent from 'hooks/useUserEngagementEvent';
import { USER_ENGAGEMENT_EVENT_VERBS } from 'constants/userEngagementEventVerbs';

interface PlaylistProps {
  playlist: PlaylistRecord & PlaylistCurrentTrack;
  currentVideoId: number;
  setCurrentLearningResource: (playlist: LearningTracks) => void;
  isIntro: boolean;
}

function Playlist({
  playlist,
  currentVideoId,
  setCurrentLearningResource,
  isIntro
}: PlaylistProps) {
  const currentUser = useCurrentUser();
  const ageLevelsTags = filterTagsByGroup('age_group');
  const classVersionsTags = filterTagsByGroup('class_version');

  function filterTagsByGroup(group: string) {
    return playlist.learning_tracks.flatMap(
      (learningTrack: LearningTracks) =>
        learningTrack.learning_resources.tags
          ?.filter((tag: any) => tag.group === group)
          .map((tag: any) => ({
            ...tag,
            learningTrackID: learningTrack.id
          })) || []
    );
  }

  useUserEngagementEvent({
    objectId: currentVideoId,
    objectType: 'LearningResourceRecord',
    userId: currentUser.id,
    verb: USER_ENGAGEMENT_EVENT_VERBS.FIRST_VIEWS,
    ready: !isIntro
  });

  if (!playlist) {
    return null;
  }

  const fallbackImage =
    'https://dashboard.teachstone.com/static/media/teachstone-bg.19a12ea37554c05f9ffb.jpg';

  return (
    <>
      {playlist.learning_tracks.map(
        (track: LearningTracks, idx: number, arr: LearningTracks[]) => {
          // First track is always open, others depend on previous completion
          const prevRealTrack = arr
            .slice(0, idx)
            .reverse()
            .find(t => t.type !== 'intro');
          const isOpen =
            idx === 0 || !prevRealTrack || !!prevRealTrack.completed_at;
          return (
            <PlaylistCard
              key={track.id}
              isIntro={track.type === 'intro'}
              introImage={playlist.intro_url || fallbackImage}
              fallbackImage={fallbackImage}
              track={track}
              isActive={currentVideoId === track.learning_resources.id}
              isOpen={isOpen}
              onSelect={
                isOpen
                  ? selectedTrack =>
                      setCurrentLearningResource(
                        getNextLearningResource(playlist, selectedTrack.id)
                      )
                  : undefined
              }
              classVersionsTags={classVersionsTags}
              ageLevelsTags={ageLevelsTags}
            />
          );
        }
      )}
    </>
  );
}

export default Playlist;
